import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMyProperties, getPropertyUnits } from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import {
  getPropertyMaintenanceRequests,
  getUnitMaintenanceRequests,
  updateMaintenanceRequest,
} from '../../../api/services/maintenanceService';
import Modal from '../../../components/Modal/Modal';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './PropertyMaintenanceView.css';

const STATUS_FILTERS = ['OPEN', 'HANDLED', 'COMPLETED', 'CANCELLED'];
const MAINTENANCE_PROPERTY_QUERY_KEY = 'maintenanceProperty';

function propertiesFromAgentAssignmentsResponse(res) {
  const d = res?.data;
  const list = Array.isArray(d?.content) ? d.content : Array.isArray(d) ? d : [];
  const byId = new Map();
  list.forEach((a) => {
    if (!a?.propertyId || byId.has(a.propertyId)) return;
    byId.set(a.propertyId, {
      id: a.propertyId,
      name: a.propertyName || 'Property',
      address: a.propertyAddress || '',
    });
  });
  return [...byId.values()];
}

function unitsFromApiResponse(res) {
  if (!res || res.status !== 'success' || !res.data) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.units)) return d.units;
  if (Array.isArray(d.data)) return d.data;
  return [];
}

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel = (status) => {
  const value = String(status || '').toUpperCase();
  if (value === 'OPEN') return 'Open';
  if (value === 'HANDLED') return 'Handled';
  if (value === 'COMPLETED') return 'Completed';
  if (value === 'CANCELLED') return 'Cancelled';
  return value || 'Unknown';
};

const getAllowedStatuses = (currentStatus) => {
  const current = String(currentStatus || '').toUpperCase();
  if (current === 'OPEN') return ['OPEN', 'HANDLED', 'COMPLETED', 'CANCELLED'];
  if (current === 'HANDLED') return ['HANDLED', 'COMPLETED', 'CANCELLED'];
  if (current === 'COMPLETED') return ['COMPLETED'];
  if (current === 'CANCELLED') return ['CANCELLED'];
  return STATUS_FILTERS;
};

const PropertyMaintenanceView = ({ isAgentView = false }) => {
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [propertyError, setPropertyError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitIdFilter, setUnitIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('OPEN');

  const [maintenanceRows, setMaintenanceRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editStatus, setEditStatus] = useState('OPEN');
  const [editNotes, setEditNotes] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const selectedPropertyRef = useRef(null);
  selectedPropertyRef.current = selectedProperty;

  const buildUrlWithPropertyParam = (propertyId) => {
    const url = new URL(window.location.href);
    if (propertyId) {
      url.searchParams.set(MAINTENANCE_PROPERTY_QUERY_KEY, String(propertyId));
    } else {
      url.searchParams.delete(MAINTENANCE_PROPERTY_QUERY_KEY);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingProperties(true);
        setPropertyError('');
        if (isAgentView) {
          // Fetch both assigned properties AND personal properties in parallel
          const [assignmentsRes, personalRes] = await Promise.allSettled([
            getMyAgentAssignments({ page: 0, size: 200 }),
            getMyProperties({ page: 0, size: 200, search: '' }),
          ]);

          const assignedProperties = assignmentsRes.status === 'fulfilled'
            ? propertiesFromAgentAssignmentsResponse(assignmentsRes.value)
            : [];

          const personalProperties = personalRes.status === 'fulfilled'
            ? (Array.isArray(personalRes.value?.data?.content)
                ? personalRes.value.data.content.map((p) => ({ id: p.id, name: p.name || 'Property', address: p.address || '' }))
                : [])
            : [];

          // Merge, deduplicating by ID
          const seenIds = new Set();
          const merged = [];
          [...personalProperties, ...assignedProperties].forEach((p) => {
            if (p?.id && !seenIds.has(p.id)) {
              seenIds.add(p.id);
              merged.push(p);
            }
          });

          if (!cancelled) setProperties(merged);
          return;
        }

        const response = await getMyProperties({ page: 0, size: 200, search: '' });
        const content = Array.isArray(response?.data?.content) ? response.data.content : [];
        if (!cancelled) setProperties(content);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setPropertyError(error.message || 'Failed to load properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) setLoadingProperties(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAgentView]);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setUnits([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getPropertyUnits(selectedProperty.id);
        if (!cancelled) setUnits(unitsFromApiResponse(res));
      } catch (error) {
        console.error(error);
        if (!cancelled) setUnits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProperty]);

  const loadMaintenanceRows = async (propertyId, unitId = '') => {
    try {
      setLoadingRows(true);
      setRowsError('');
      const response = unitId
        ? await getUnitMaintenanceRequests(propertyId, unitId)
        : await getPropertyMaintenanceRequests(propertyId);
      const rows = Array.isArray(response?.data) ? response.data : [];
      rows.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      setMaintenanceRows(rows);
    } catch (error) {
      setRowsError(error.message || 'Failed to load maintenance requests.');
      setMaintenanceRows([]);
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    if (!selectedProperty?.id) return;
    loadMaintenanceRows(selectedProperty.id, unitIdFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, unitIdFilter]);

  // Ensure browser/device back goes one step to property list first.
  useEffect(() => {
    const onPopState = () => {
      if (selectedPropertyRef.current?.id) {
        setSelectedProperty(null);
        setMaintenanceRows([]);
        setRowsError('');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Restore selected property if query param exists (e.g., browser refresh while in detail view).
  useEffect(() => {
    if (selectedProperty) return;
    const url = new URL(window.location.href);
    const propertyFromQuery = url.searchParams.get(MAINTENANCE_PROPERTY_QUERY_KEY);
    if (!propertyFromQuery) return;
    const property = properties.find((p) => String(p?.id) === String(propertyFromQuery));
    if (property) {
      setSelectedProperty({ id: property.id, name: property.name || 'Property' });
    }
  }, [properties, selectedProperty]);

  const filteredProperties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) => {
      const name = String(p?.name || '').toLowerCase();
      const address = String(p?.address || '').toLowerCase();
      return name.includes(q) || address.includes(q);
    });
  }, [properties, searchQuery]);

  const statusCounts = useMemo(() => {
    const counts = { OPEN: 0, HANDLED: 0, COMPLETED: 0, CANCELLED: 0 };
    maintenanceRows.forEach((row) => {
      const key = String(row.status || '').toUpperCase();
      if (counts[key] != null) counts[key] += 1;
    });
    return counts;
  }, [maintenanceRows]);

  const visibleRows = useMemo(
    () => maintenanceRows.filter((row) => String(row.status || '').toUpperCase() === statusFilter),
    [maintenanceRows, statusFilter]
  );

  const openUpdateModal = (row) => {
    const currentStatus = String(row?.status || '').toUpperCase() || 'OPEN';
    setEditingRow(row);
    setEditStatus(currentStatus);
    setEditNotes(row?.landlordNotes || '');
    setUpdateError('');
  };

  const closeUpdateModal = () => {
    if (updateSubmitting) return;
    setEditingRow(null);
    setUpdateError('');
  };

  const handleUpdateSubmit = async () => {
    if (!editingRow?.id) return;
    try {
      setUpdateSubmitting(true);
      setUpdateError('');
      await updateMaintenanceRequest(editingRow.id, {
        status: editStatus,
        landlordNotes: editNotes,
      });
      await loadMaintenanceRows(selectedProperty.id, unitIdFilter);
      setEditingRow(null);
    } catch (error) {
      setUpdateError(error.message || 'Failed to update maintenance request.');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const handleBackToPropertyList = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const nextUrl = buildUrlWithPropertyParam(null);
    window.history.replaceState(window.history.state, '', nextUrl);
    setSelectedProperty(null);
    setMaintenanceRows([]);
    setRowsError('');
    setUnitIdFilter('');
    setStatusFilter('OPEN');
  };

  if (!selectedProperty) {
    return (
      <div className="property-maintenance-view">
        <CollapsibleTopPanel id="maintenance-list-top" defaultOpenOnMobile={false}>
          <div className="property-maintenance-view__header">
            <h1>Maintenance</h1>
            <input
              type="text"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CollapsibleTopPanel>

        {propertyError ? <div className="property-maintenance-view__state error">{propertyError}</div> : null}

        {loadingProperties ? (
          <div className="property-maintenance-view__state">Loading properties...</div>
        ) : filteredProperties.length === 0 ? (
          <div className="property-maintenance-view__state">
            {isAgentView
              ? 'No properties found. Assigned or personal properties will appear here.'
              : 'No properties found. Add a property in Manage Rentals to view maintenance requests.'}
          </div>
        ) : (
          <div className="property-maintenance-view__property-grid">
            {filteredProperties.map((property) => (
              <button
                key={property.id}
                type="button"
                className="property-maintenance-view__property-card"
                onClick={() => {
                  const nextUrl = buildUrlWithPropertyParam(property.id);
                  window.history.pushState(
                    { maintenancePropertyId: property.id, section: 'maintenance' },
                    '',
                    nextUrl
                  );
                  setSelectedProperty({ id: property.id, name: property.name || 'Property' });
                  setUnitIdFilter('');
                  setStatusFilter('OPEN');
                }}
              >
                <h3>{property.name || 'Property'}</h3>
                <p>{property.address || 'Open to view maintenance requests'}</p>
                <span>View maintenance requests →</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="property-maintenance-view">
      <div className="property-maintenance-view__back-row">
        <button
          type="button"
          className="property-maintenance-view__back-btn"
          onClick={handleBackToPropertyList}
          aria-label="Back to properties"
        >
          ← Back to properties
        </button>
      </div>
      <CollapsibleTopPanel id="maintenance-detail-top" defaultOpenOnMobile={false}>
        <div className="property-maintenance-view__header property-maintenance-view__header--detail">
          <div>
            <h1>Maintenance</h1>
            <p>{selectedProperty.name}</p>
          </div>
        </div>

        <div className="property-maintenance-view__toolbar">
          <div className="property-maintenance-view__status-filters">
            {STATUS_FILTERS.map((status) => (
              <button
                key={status}
                type="button"
                className={`property-maintenance-view__status-btn${statusFilter === status ? ' active' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                {statusLabel(status)} ({statusCounts[status] || 0})
              </button>
            ))}
          </div>

          <div className="property-maintenance-view__unit-filter">
            <label htmlFor="maintenance-unit-filter">Unit</label>
            <select
              id="maintenance-unit-filter"
              value={unitIdFilter}
              onChange={(e) => setUnitIdFilter(e.target.value)}
            >
              <option value="">All units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.unitNumber || unit.name || unit.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      {rowsError ? <div className="property-maintenance-view__state error">{rowsError}</div> : null}

      {loadingRows ? (
        <div className="property-maintenance-view__state">Loading maintenance requests...</div>
      ) : visibleRows.length === 0 ? (
        <div className="property-maintenance-view__state">
          No {statusLabel(statusFilter).toLowerCase()} requests found for this selection.
        </div>
      ) : (
        <div className="property-maintenance-view__list">
          {visibleRows.map((row) => {
            const status = String(row.status || '').toUpperCase();
            return (
              <article key={row.id} className="property-maintenance-view__item">
                <div className="property-maintenance-view__item-top">
                  <h3>{row.description || 'Maintenance request'}</h3>
                  <span className={`property-maintenance-view__badge property-maintenance-view__badge--${status.toLowerCase()}`}>
                    {statusLabel(status)}
                  </span>
                </div>
                <p className="property-maintenance-view__meta">
                  Unit {row.unitNumber || '—'} • Requested by {row.requestedByName || row.requestedByEmail || 'Tenant'}
                </p>
                <p className="property-maintenance-view__meta">Submitted: {formatDateTime(row.createdAt)}</p>
                {row.landlordNotes ? (
                  <p className="property-maintenance-view__notes">Notes: {row.landlordNotes}</p>
                ) : null}
                {row.resolvedAt ? (
                  <p className="property-maintenance-view__meta">Resolved: {formatDateTime(row.resolvedAt)}</p>
                ) : null}
                <div className="property-maintenance-view__actions">
                  <button
                    type="button"
                    className="property-maintenance-view__update-btn"
                    onClick={() => openUpdateModal(row)}
                  >
                    Update
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {editingRow ? (
        <Modal onClose={closeUpdateModal} className="medium" ariaLabel="Update maintenance request">
          <h3 className="property-maintenance-view__modal-title">Update Maintenance</h3>
          <p className="property-maintenance-view__modal-subtitle">
            Unit {editingRow.unitNumber || '—'} · {editingRow.requestedByName || editingRow.requestedByEmail || 'Tenant'}
          </p>
          <div className="property-maintenance-view__modal-body">
            <label htmlFor="maintenance-status">Status</label>
            <select
              id="maintenance-status"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              disabled={updateSubmitting}
            >
              {getAllowedStatuses(editingRow.status).map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>

            <label htmlFor="maintenance-notes">Landlord notes (optional)</label>
            <textarea
              id="maintenance-notes"
              rows={4}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              disabled={updateSubmitting}
              placeholder="Add progress notes for tenant visibility"
            />
            {updateError ? <p className="property-maintenance-view__update-error">{updateError}</p> : null}
          </div>
          <div className="property-maintenance-view__modal-actions">
            <button type="button" className="btn secondary" onClick={closeUpdateModal} disabled={updateSubmitting}>
              Cancel
            </button>
            <button type="button" className="btn primary" onClick={handleUpdateSubmit} disabled={updateSubmitting}>
              {updateSubmitting ? 'Updating...' : 'Save Update'}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default PropertyMaintenanceView;
